import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CommunityPost } from '../types/community';
import '../styles/Community.scss';

import icon from '../assets/community.svg';
import icon1 from '../assets/icon_edit.svg';
import view from '../assets/Icon_view.svg';
import comment from '../assets/Comment_Icon.svg';
import like from '../assets/Like_Icon.svg';

const CommunityListPage = () => {
  const [posts, setPosts] = useState<CommunityPost[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<CommunityPost[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState('latest'); // 'latest' or 'most_liked'

  const categories = [
    '모든 사연',
    '연애',
    'LGBT',
    '외모',
    '대인관계/따돌림',
    '정신건강',
    '성생활',
    '취업/진로',
    '학업/고시',
    '이별/이혼',
    '부부관계',
    '펫로스',
    '성범죄',
    '신체건강',
    '육아/출산',
    '직장',
    '금전/사업',
    '자아/성격',
    '가족',
    '기타',
  ];

  const [searchParams] = useSearchParams();

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get<CommunityPost[]>('/api/community');
        setPosts(response.data);
      } catch (error) {
        console.error('Error fetching posts:', error);
      }
    };

    fetchPosts();
  }, []);

  useEffect(() => {
    let result = posts;
    const tag = searchParams.get('tag');

    if (tag) {
      result = result.filter(p => p.hashtags.includes(`#${tag}`));
    }

    if (selectedCategories.length > 0) {
      result = result.filter(post => selectedCategories.includes(post.category));
    }

    if (searchTerm) {
      result = result.filter(
        post =>
          post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          post.content.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sorting logic
    if (sortBy === 'latest') {
      result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    } else if (sortBy === 'most_liked') {
      result.sort((a, b) => b.likes.length - a.likes.length);
    }

    setFilteredPosts(result);
  }, [posts, selectedCategories, searchTerm, searchParams, sortBy]);

  const handleCategoryClick = (category: string) => {
    if (category === '모든 사연') {
      setSelectedCategories([]);
      return;
    }

    setSelectedCategories(prev => {
      if (prev.includes(category)) {
        return prev.filter(c => c !== category);
      } else {
        return [...prev, category];
      }
    });
  };

  return (
    <div className="main-container">
      <div className="community-container app-container">
        <div className="page-header">
          <img src={icon} alt="Mind Thermometer Icon" className="page-header__icon" />
          <h2 className="page-header__title">커뮤니티</h2>
        </div>

        <div className="filter-container">
          <div className="search-bar">
            <input
              type="text"
              placeholder="검색어를 입력하세요..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="sort-options">
            <div>
              <button
                className={`sort-btn ${sortBy === 'latest' ? 'active' : ''}`}
                onClick={() => setSortBy('latest')}
              >
                최신순
              </button>
              <button
                className={`sort-btn ${sortBy === 'most_liked' ? 'active' : ''}`}
                onClick={() => setSortBy('most_liked')}
              >
                공감순
              </button>
            </div>

            <Link to="/community/write" className="edit-link">
              <img src={icon1} alt="글쓰기" />
              <p>글쓰기</p>
            </Link>
          </div>
          <div className="filter-options">
            {categories.map(category => (
              <button
                key={category}
                className={`category-btn ${
                  (category === '모든 사연' && selectedCategories.length === 0) ||
                  (category !== '모든 사연' && selectedCategories.includes(category))
                    ? 'active'
                    : ''
                }`}
                onClick={() => handleCategoryClick(category)}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        <div className="community-card">
          {filteredPosts.map(post => (
            <Link to={`/community/${post._id}`} key={post._id} className="community-card__item">
              <div>
                <div className="community-card__category">
                  <p>{post.category}</p>
                </div>
                <h3 className="community-card__title">{post.title}</h3>
                <div className="community-card__meta">
                  <span>{post.user?.displayName || '삭제된 사용자'}</span>
                  <span>{new Date(post.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
              <div className="community-card__middle">
                <div
                  className="community-card__snippet"
                  dangerouslySetInnerHTML={{ __html: post.content }}
                ></div>
                {post.hashtags && post.hashtags.length > 0 && (
                  <div className="community-card__hashtags">
                    {post.hashtags.map(tag => (
                      <span key={tag}>{tag}</span>
                    ))}
                  </div>
                )}
              </div>
              <div className="community-card__footer">
                <div className="community-card__stats">
                  <span>
                    <img src={like} alt="공감" /> <p>{post.likes.length}</p>
                  </span>
                  <span>
                    <img src={comment} alt="댓글" /> <p> {post.comments.length}</p>
                  </span>
                  <span>
                    <img src={view} alt="조회" /> <p>{post.views}</p>
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CommunityListPage;
